import React, { Component, ErrorInfo, ReactNode } from 'react';
import { Box, Typography, Button, Alert } from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';

interface Props {
  children: ReactNode;
  fallback?: React.ComponentType<{ error: Error; resetError: () => void }>;
}

interface State {
  hasError: boolean;
  error: Error | null;
}

class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error('Error caught by ErrorBoundary:', error);
    console.error('Error type:', typeof error);
    console.error('Error message:', error.message);
    console.error('Error stack:', error.stack);
    console.error('Error info:', errorInfo);
  }

  resetError = () => {
    this.setState({ hasError: false, error: null });
    window.location.reload();
  };

  render() {
    if (this.state.hasError) {
      console.log('ErrorBoundary render - error state:', this.state.error);
      console.log('ErrorBoundary render - error type:', typeof this.state.error);
      console.log('ErrorBoundary render - error message:', this.state.error?.message);
      
      if (this.props.fallback) {
        const FallbackComponent = this.props.fallback;
        return <FallbackComponent error={this.state.error!} resetError={this.resetError} />;
      }

      return (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            minHeight: '400px',
            p: 3,
            textAlign: 'center',
          }}
        >
          <Alert severity="error" sx={{ mb: 2, maxWidth: '600px' }}>
            <Typography variant="h6" sx={{ mb: 1 }}>
              Đã xảy ra lỗi
            </Typography>
            <Typography variant="body2" sx={{ mb: 2 }}>
              {this.state.error 
                ? (typeof this.state.error === 'string' 
                    ? this.state.error 
                    : this.state.error.message 
                      ? (typeof this.state.error.message === 'string' 
                          ? this.state.error.message 
                          : JSON.stringify(this.state.error.message))
                      : JSON.stringify(this.state.error))
                : 'Có lỗi không mong muốn xảy ra. Vui lòng thử lại.'}
            </Typography>
            <Button
              variant="contained"
              startIcon={<RefreshIcon />}
              onClick={this.resetError}
              sx={{ mt: 1 }}
            >
              Thử lại
            </Button>
          </Alert>
        </Box>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
